import { byDisplayOrder } from './platformOrder.js';
import { asRecord, list, num, records, strings, text } from './resultValues.js';

export interface ScoredBatchPlatform {
  networkId: number;
  slug: string;
  label: string;
  score: number | null;
  scoredBy: string | null;
  attributes: Array<{ name: string; percent: number }>;
  halves: Array<{ label: string; value: number }>;
  analysed: number;
  needsAttention: number;
}

export interface ScoredCreative {
  creativeId: string;
  networkId: number;
  name: string;
  score: number | null;
  status: string;
  error: string | null;
  thumbnailUri: string | null;
  creativeType: string;
  extension: string | null;
  width: number | null;
  height: number | null;
  duration: number | null;
  attributes: Array<{ name: string; percent: number }>;
}

export const RECALC_PREFLIGHT = 'recalcPreflightCreativeScores';

const TIME_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
};

export const ATTENTION_BELOW = 5;

// ponytail: half of the 0-4 feature scale; move it to the scoring sheets if product wants it tunable.
export const WEAK_FEATURE_BELOW = 2;

export const SCORE_WEIGHTS: Record<string, { performance: number; bestPractice: number }> = {
  both: { performance: 70, bestPractice: 30 },
  performance: { performance: 100, bestPractice: 0 },
  best_practice: { performance: 0, bestPractice: 100 },
};

const SCORED_ON: Record<string, string> = {
  best_practice: 'Scored on best practice',
  performance: 'Scored on historical performance',
  both: 'Scored on best practice & historical performance',
};

const SCORED_BY: Record<string, string> = {
  best_practice: 'Scored by best practice',
  performance: 'Scored by performance',
  both: 'Scored by best practice & performance',
};

function attributesOf(scores: unknown): Array<{ name: string; percent: number }> {
  const holder = asRecord(scores);
  if (holder === null) return [];
  return Object.entries(holder)
    .flatMap(([name, value]) => {
      const percent = num(value);
      return percent === null ? [] : [{ name, percent }];
    })
    .sort((left, right) => left.name.localeCompare(right.name));
}

function extensionOf(name: string): string | null {
  const dot = name.lastIndexOf('.');
  return dot === -1 || dot === name.length - 1 ? null : name.slice(dot + 1).toUpperCase();
}

export function scoredCreatives(result: unknown): ScoredCreative[] {
  return records(asRecord(result)?.['creatives']).flatMap((row) => {
    const creativeId = text(row['creative_id']);
    const networkId = num(row['network_id']);
    if (creativeId === null || networkId === null) return [];
    return [
      {
        creativeId,
        networkId,
        name: text(row['name']) ?? creativeId,
        score: num(row['final_overall_score']),
        status: text(row['status']) ?? 'Completed',
        error: text(row['error']),
        thumbnailUri: text(row['thumbnail_uri']),
        creativeType: text(row['creative_type']) ?? 'Banner',
        extension: extensionOf(text(row['name']) ?? ''),
        width: num(row['width']),
        height: num(row['height']),
        duration: num(row['duration']),
        attributes: attributesOf(row['attribute_scores']),
      },
    ];
  });
}

function halvesOf(row: Record<string, unknown>): Array<{ label: string; value: number }> {
  return [
    { label: 'Brand guidelines', value: num(row['brand_guidelines_overall_score']) },
    { label: 'Best practice', value: num(row['best_practice_overall_score']) },
    { label: 'Performance', value: num(row['performance_overall_score']) },
  ].flatMap((half) => (half.value === null ? [] : [{ label: half.label, value: half.value }]));
}

function scoredBy(row: Record<string, unknown>): string | null {
  const halves = [
    num(row['performance_overall_score']) === null ? null : 'performance',
    num(row['best_practice_overall_score']) === null ? null : 'best practice',
  ].filter((half) => half !== null);
  if (halves.length === 0) return null;
  return `Scored by ${[...halves].reverse().join(' & ')}`;
}

export function scoredPlatforms(
  result: unknown,
  platformOf: (networkId: number) => { slug: string; label: string },
): ScoredBatchPlatform[] {
  const creatives = scoredCreatives(result);
  const platforms = records(asRecord(result)?.['batch_scores']).flatMap((row) => {
    const networkId = num(row['network_id']);
    if (networkId === null) return [];
    const scored = creatives.filter(
      (creative) => creative.networkId === networkId && creative.status !== 'Failed',
    );
    return [
      {
        networkId,
        ...platformOf(networkId),
        score: num(row['final_overall_score']),
        scoredBy: scoredBy(row),
        attributes: attributesOf(row['attribute_scores']),
        halves: halvesOf(row),
        analysed: scored.length,
        needsAttention: scored.filter(
          (creative) => creative.score !== null && creative.score < ATTENTION_BELOW,
        ).length,
      },
    ];
  });
  return byDisplayOrder(platforms);
}

export function creativesOn(result: unknown, networkId: number): ScoredCreative[] {
  return scoredCreatives(result).filter((creative) => creative.networkId === networkId);
}

export function creativeLine(creative: ScoredCreative): string {
  const size =
    creative.width === null || creative.height === null
      ? null
      : `${creative.width}×${creative.height}`;
  const seconds = creative.duration === null || creative.duration <= 0 ? null : creative.duration;
  const clock =
    seconds === null
      ? null
      : `${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, '0')} sec`;
  return [creative.extension, clock ?? size].filter((part) => part !== null).join(' · ');
}

export function whenCompleted(completedAt: string | null | undefined): string | null {
  if (completedAt == null) return null;
  const at = new Date(completedAt);
  return Number.isNaN(at.getTime()) ? null : at.toLocaleString(undefined, TIME_FORMAT);
}

export function batchName(result: unknown): string | null {
  return text(asRecord(result)?.['name']);
}

export function requestedScoringLine(result: unknown): string | null {
  const mode = text(asRecord(result)?.['scoring_mode']);
  return mode === null ? null : (SCORED_BY[mode] ?? null);
}

export type CreativeOrder = 'high' | 'low' | 'attention' | 'name';

export function creativesAcross(result: unknown): ScoredCreative[] {
  const byCreative = new Map<string, ScoredCreative>();
  for (const creative of scoredCreatives(result)) {
    if (!byCreative.has(creative.creativeId)) byCreative.set(creative.creativeId, creative);
  }
  return [...byCreative.values()];
}

export function inOrder(creatives: ScoredCreative[], order?: string[]): ScoredCreative[] {
  if (order === undefined) return creatives;
  const rank = new Map(order.map((creativeId, at) => [creativeId, at]));
  return creatives
    .filter((creative) => rank.has(creative.creativeId))
    .sort((left, right) => (rank.get(left.creativeId) ?? 0) - (rank.get(right.creativeId) ?? 0));
}

export function sortedCreatives(
  creatives: ScoredCreative[],
  order: CreativeOrder,
): ScoredCreative[] {
  return [...creatives].sort((left, right) => {
    if (order === 'name') return left.name.localeCompare(right.name);
    if (order === 'attention') {
      const flagged = needsAttention(right) ? 1 : 0;
      const already = needsAttention(left) ? 1 : 0;
      if (flagged !== already) return flagged - already;
    }
    if (left.score === null || right.score === null) {
      return left.score === right.score ? 0 : left.score === null ? 1 : -1;
    }
    return order === 'low' ? left.score - right.score : right.score - left.score;
  });
}

export function needsAttention(creative: ScoredCreative): boolean {
  return creative.score !== null && creative.score < ATTENTION_BELOW;
}

export interface CreativePlatformScore {
  networkId: number;
  slug: string;
  label: string;
  score: number | null;
  halves: Array<{ label: string; value: number }>;
}

export function creativeScores(
  result: unknown,
  creativeId: string,
  platformOf: (networkId: number) => { slug: string; label: string },
): CreativePlatformScore[] {
  const rows = records(asRecord(result)?.['creatives']).flatMap((row) => {
    if (text(row['creative_id']) !== creativeId) return [];
    const networkId = num(row['network_id']);
    if (networkId === null) return [];
    return [
      {
        networkId,
        ...platformOf(networkId),
        score: num(row['final_overall_score']),
        halves: halvesOf(row),
      },
    ];
  });
  return byDisplayOrder(rows);
}

export function scoredOnLine(result: unknown): string | null {
  const mode = text(asRecord(result)?.['scoring_mode']);
  return mode === null ? null : (SCORED_ON[mode] ?? null);
}

export interface CreativeHalves {
  score: number | null;
  performance: { score: number | null; weight: number };
  bestPractice: { score: number | null; weight: number };
}

export function creativeHalves(
  result: unknown,
  creativeId: string,
  networkId: number,
): CreativeHalves | null {
  const row = records(asRecord(result)?.['creatives']).find(
    (entry) => text(entry['creative_id']) === creativeId && num(entry['network_id']) === networkId,
  );
  if (row === undefined) return null;
  const performance = num(row['performance_overall_score']);
  // No history: the engine scores the creative on best practice alone, whatever the mode.
  const mode =
    performance === null ? 'best_practice' : (text(asRecord(result)?.['scoring_mode']) ?? 'both');
  const weights = SCORE_WEIGHTS[mode] ?? SCORE_WEIGHTS['both'];
  return {
    score: num(row['final_overall_score']),
    performance: {
      score: performance,
      weight: weights?.performance ?? 0,
    },
    bestPractice: {
      score: num(row['best_practice_overall_score']),
      weight: weights?.bestPractice ?? 0,
    },
  };
}

export function scoringModeOf(result: unknown): string | null {
  return text(asRecord(result)?.['scoring_mode']);
}

export function primaryKpi(result: unknown): string | null {
  const [first] = strings(asRecord(result)?.['kpis']);
  return first ?? null;
}

export function campaignMetadataOf(
  result: unknown,
  creativeId: string,
  networkId: number,
): Record<string, string> {
  const row = records(asRecord(result)?.['creatives']).find(
    (entry) => text(entry['creative_id']) === creativeId && num(entry['network_id']) === networkId,
  );
  const metadata = row === undefined ? null : asRecord(row['campaign_metadata']);
  if (metadata === null) return {};
  return Object.fromEntries(
    Object.entries(metadata).flatMap(([key, value]) =>
      typeof value === 'string' ? [[key, value]] : [],
    ),
  );
}

export function mergeRescored(result: unknown, rescored: unknown): unknown {
  const batch = asRecord(result);
  const update = asRecord(rescored);
  if (batch === null || update === null) return result;
  const rows = records(update['creatives']);
  const scores = records(update['batch_scores']);
  const replaced = (
    original: Record<string, unknown>,
    incoming: Array<Record<string, unknown>>,
    sameRow: (left: Record<string, unknown>, right: Record<string, unknown>) => boolean,
  ) => incoming.find((row) => sameRow(row, original)) ?? original;

  return {
    ...batch,
    creatives: list(batch['creatives']).map((entry) => {
      const row = asRecord(entry);
      if (row === null) return entry;
      return replaced(
        row,
        rows,
        (left, right) =>
          left['creative_id'] === right['creative_id'] &&
          left['network_id'] === right['network_id'],
      );
    }),
    batch_scores: list(batch['batch_scores']).map((entry) => {
      const row = asRecord(entry);
      if (row === null) return entry;
      return replaced(row, scores, (left, right) => left['network_id'] === right['network_id']);
    }),
  };
}

export interface BatchFix {
  attribute: string;
  feature: string;
  recommendation: string;
  creatives: string[];
  averageScore: number;
}

export function batchFixes(result: unknown, networkId: number): BatchFix[] {
  const fixes = new Map<string, BatchFix & { ids: Set<string>; total: number; rows: number }>();
  for (const row of records(asRecord(result)?.['creatives'])) {
    if (num(row['network_id']) !== networkId || text(row['status']) === 'Failed') continue;
    const creativeId = text(row['creative_id']) ?? '';
    const name = text(row['name']) ?? creativeId;
    for (const entry of records(row['features'])) {
      const feature = text(entry['feature']);
      const recommendation = text(entry['recommendation']);
      const score = num(entry['final_feature_score']);
      if (feature === null || recommendation === null || score === null) continue;
      if (score >= WEAK_FEATURE_BELOW) continue;
      const key = `${feature}\u0000${recommendation}`;
      const fix = fixes.get(key) ?? {
        attribute: text(entry['attribute']) ?? '',
        feature,
        recommendation,
        creatives: [],
        averageScore: 0,
        ids: new Set<string>(),
        total: 0,
        rows: 0,
      };
      if (!fix.ids.has(creativeId)) {
        fix.ids.add(creativeId);
        fix.creatives.push(name);
      }
      fix.total += score;
      fix.rows += 1;
      fix.averageScore = fix.total / fix.rows;
      fixes.set(key, fix);
    }
  }
  return [...fixes.values()]
    .map(({ ids: _ids, total: _total, rows: _rows, ...fix }) => fix)
    .sort(
      (left, right) =>
        right.creatives.length - left.creatives.length || left.averageScore - right.averageScore,
    );
}

export function failedCreatives(result: unknown): ScoredCreative[] {
  return scoredCreatives(result).filter((creative) => creative.status === 'Failed');
}
